import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { useThemeStore } from './stores/themeStore'
import { AuthProvider } from './core/AuthContext'
import ProtectedRoute from './core/ProtectedRoute'

// Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import HomePage from './pages/HomePage'
import NotFoundPage from './pages/NotFoundPage'

// Public Pages
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'

// Report Workflow
import NewReportPage from './pages/reports/NewReportPage'
import MyReportsPage from './pages/reports/MyReportsPage'
import ReportDetailPage from './pages/reports/ReportDetailPage'

// Layout
import AppShell from './components/AppShell'

// Admin
import AdminShell from './admin/AdminShell'
// import AdminDashboard from './admin/pages/AdminDashboard'
// import AdminReports from './admin/pages/AdminReports'
// import AdminReportDetail from './admin/pages/AdminReportDetail'
// import AdminAuthorities from './admin/pages/AdminAuthorities'

// Help
import HelpShell from './help/HelpShell'

// Settings
import SettingsPage from './pages/SettingsPage'
import ChangePasswordPage from './pages/settings/ChangePasswordPage'

// Initialize React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Public app routes — guests can report without an account (verified email required) */}
      <Route element={<AppShell />}>
        <Route path="/report/new" element={<NewReportPage />} />
      </Route>

      {/* Protected Routes - Authenticated Users (wrapped in app shell) */}
      <Route element={<ProtectedRoute requireAuth />}>
        <Route element={<AppShell />}>
          <Route path="/home" element={<HomePage />} />
          <Route path="/reports" element={<MyReportsPage />} />
          <Route path="/reports/:id" element={<ReportDetailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/change-password" element={<ChangePasswordPage />} />
          <Route path="/help/*" element={<HelpShell />} />
        </Route>
      </Route>

      {/* Admin Routes - TODO: Create admin pages */}
      {/* <Route element={<ProtectedRoute requireAuth requireAdmin />}>
        <Route path="/admin" element={<AdminShell />}>
          <Route index element={<AdminDashboard />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="reports/:id" element={<AdminReportDetail />} />
          <Route path="authorities" element={<AdminAuthorities />} />
        </Route>
      </Route> */}

      {/* Catch all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default function App() {
  const theme = useThemeStore((state) => state.theme)
  const setTheme = useThemeStore((state) => state.setTheme)

  // Initialize theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-storage')
    if (savedTheme) {
      try {
        const { theme } = JSON.parse(savedTheme).state
        setTheme(theme)
      } catch (e) {
        setTheme('dark')
      }
    } else {
      // Brand is dark-first — default to dark regardless of system preference
      setTheme('dark')
    }
  }, [setTheme])

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  )
}
